// Console i18n (rhino-ops-console#295, Contract C5 of dnm-work#149).
// i18next + react-i18next, one JSON catalog per language in ./locales. English is the template and the
// fallback and is bundled eagerly (R45): a failed chunk can never blank the console. Every other
// language stays lazy (one chunk each: Hindi is never fetched while English is active). A key missing
// everywhere renders as empty text, never as the key.
import i18next, { type BackendModule, type i18n as I18n } from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';

export const DEFAULT_LANGUAGE = 'en';
export const SUPPORTED_LANGUAGES = ['en', 'hi'] as const;
/** Languages a user can pick. Only English until the Hindi catalog is reviewed (Contract C5, P7). */
export const SELECTABLE_LANGUAGES = ['en'] as const;

export type LocaleLoader = () => Promise<unknown>;

/** One lazy loader per `./locales/<lang>.json`, keyed by language. */
export function localeLoaders(): Record<string, LocaleLoader> {
  const files = import.meta.glob('./locales/*.json');
  return Object.fromEntries(
    Object.entries(files).map(([path, load]) => [path.replace(/^\.\/locales\/(.+)\.json$/, '$1'), load]),
  );
}

function lazyBackend(loaders: Record<string, LocaleLoader>): BackendModule {
  return {
    type: 'backend',
    init: () => undefined,
    read(language, _namespace, callback) {
      const load = loaders[language];
      if (!load) {
        callback(null, {});
        return;
      }
      load().then(
        (mod) => callback(null, ((mod as { default?: unknown }).default ?? mod) as Record<string, unknown>),
        (error: unknown) => callback(error instanceof Error ? error : new Error(String(error)), false),
      );
    },
  };
}

/**
 * Initialises `instance` (the app's i18next by default) once. English (`english`, the bundled
 * `./locales/en.json` by default) is in memory from the start; the lazy backend serves only the
 * other languages.
 */
export async function initI18n(
  instance: I18n = i18next,
  loaders: Record<string, LocaleLoader> = localeLoaders(),
  english: Record<string, unknown> = en,
) {
  if (instance.isInitialized) return instance;
  const lazyLoaders = Object.fromEntries(Object.entries(loaders).filter(([lang]) => lang !== DEFAULT_LANGUAGE));
  await instance
    .use(initReactI18next)
    .use(lazyBackend(lazyLoaders))
    .init({
      resources: { [DEFAULT_LANGUAGE]: { translation: english } },
      partialBundledLanguages: true,
      lng: DEFAULT_LANGUAGE,
      fallbackLng: DEFAULT_LANGUAGE,
      supportedLngs: [...SUPPORTED_LANGUAGES],
      load: 'languageOnly',
      returnEmptyString: false,
      interpolation: { escapeValue: false }, // React escapes rendered text
      // Reached only when no loaded catalog (English included) has the key: show nothing, never the key.
      parseMissingKeyHandler: () => '',
    });
  return instance;
}
